/**
 * @module features/devices/components/BulkImportModal
 * @description CSV bulk provisioning with instant client-side pre-validation,
 * valid/invalid stats inspection, downloadable error reports (both client-side and server-side),
 * and row-by-row server submission.
 */
import * as React from "react";
import {
  AlertCircle,
  AlertTriangle,
  CheckCircle2,
  Download,
  FileSpreadsheet,
  FileWarning,
  Loader2,
  Upload,
  X,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { AppError } from "@/lib/api-client";
import { cn } from "@/lib/utils";
import { useBulkImport, useDownloadCsvTemplate } from "../api/useBulkImport";
import { BulkImportReport, DeviceTypeDescriptor } from "../types";
import {
  ClientValidationSummary,
  downloadErrorsCsv,
  validateCsvClientSide,
} from "../utils/csvValidator";

const MAX_BYTES = 2 * 1024 * 1024;

interface BulkImportModalProps {
  open: boolean;
  onClose: () => void;
  types: DeviceTypeDescriptor[];
}

export function BulkImportModal({ open, onClose, types }: BulkImportModalProps) {
  const [deviceType, setDeviceType] = React.useState("display_module");
  const [file, setFile] = React.useState<File | null>(null);
  const [fileContent, setFileContent] = React.useState<string | null>(null);
  const [dragging, setDragging] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [validating, setValidating] = React.useState(false);
  const [clientValidation, setClientValidation] = React.useState<ClientValidationSummary | null>(null);
  const [serverReport, setServerReport] = React.useState<BulkImportReport | null>(null);

  const inputRef = React.useRef<HTMLInputElement>(null);
  const importMutation = useBulkImport();
  const templateMutation = useDownloadCsvTemplate();

  const reset = () => {
    setFile(null);
    setFileContent(null);
    setError(null);
    setValidating(false);
    setClientValidation(null);
    setServerReport(null);
    setDragging(false);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  // Run client-side validation when file or target deviceType changes
  const runClientValidation = React.useCallback((content: string, type: string) => {
    setValidating(true);
    try {
      const summary = validateCsvClientSide(content, type);
      setClientValidation(summary);
      if (summary.fatalError) {
        setError(summary.fatalError);
      } else {
        setError(null);
      }
    } catch {
      setError("Failed to parse the CSV file on the client.");
    } finally {
      setValidating(false);
    }
  }, []);

  const pickFile = async (candidate: File | undefined) => {
    setError(null);
    setServerReport(null);
    setClientValidation(null);

    if (!candidate) return;
    if (!candidate.name.toLowerCase().endsWith(".csv")) {
      setError("Please select a valid .csv file.");
      return;
    }
    if (candidate.size > MAX_BYTES) {
      setError("The file is too large (maximum 2 MB).");
      return;
    }

    setFile(candidate);
    try {
      const text = await candidate.text();
      setFileContent(text);
      runClientValidation(text, deviceType);
    } catch {
      setError("Could not read file contents.");
    }
  };

  const handleTypeChange = (newType: string) => {
    setDeviceType(newType);
    if (fileContent) {
      runClientValidation(fileContent, newType);
    }
  };

  const downloadTemplate = async () => {
    setError(null);
    try {
      await templateMutation.mutateAsync(deviceType);
    } catch (err) {
      setError(err instanceof AppError ? err.errorDetails : "Could not download the template.");
    }
  };

  const submit = async () => {
    if (!file) return;
    setError(null);
    try {
      const report = await importMutation.mutateAsync({ deviceType, file });
      setServerReport(report);
    } catch (err) {
      setError(
        err instanceof AppError
          ? `${err.errorDetails} (Request ID: ${err.requestId})`
          : "The server import failed. Please try again."
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && handleClose()}>
      <DialogContent className="sm:max-w-[680px] max-h-[92dvh] overflow-y-auto" sheetOnMobile>
        <DialogHeader>
          <DialogTitle className="text-base">Bulk Import Devices</DialogTitle>
          <DialogDescription className="text-xs">
            Import up to 500 hardware units via CSV with instant client-side format validation,
            downloadable error reports, and server provisioning.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {error && (
            <div
              role="alert"
              className="flex items-start gap-2 p-3 text-xs rounded-xl bg-danger-soft text-danger border border-danger/20"
            >
              <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
              <span className="break-words min-w-0">{error}</span>
            </div>
          )}

          {/* Top Bar: Device Subtype selector and Template download */}
          <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
            <div className="flex-1 space-y-1.5">
              <label id="bulk_type_label" className="text-xs font-medium block">
                Target Device Type
              </label>
              <Select value={deviceType} onValueChange={handleTypeChange}>
                <SelectTrigger aria-labelledby="bulk_type_label" className="h-10 bg-card rounded-xl">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  {(types.length ? types : [{ type_id: "display_module", label: "Display Module", fields: [] }]).map((t) => (
                    <SelectItem key={t.type_id} value={t.type_id}>
                      {t.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button
              type="button"
              variant="outline"
              onClick={downloadTemplate}
              disabled={templateMutation.isPending}
              className="h-10 gap-1.5 text-xs rounded-xl shrink-0"
            >
              {templateMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
              Download Template
            </Button>
          </div>

          {/* Drag & Drop File Upload Area */}
          <div
            onDragOver={(e) => {
              e.preventDefault();
              setDragging(true);
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragging(false);
              pickFile(e.dataTransfer.files?.[0]);
            }}
            className={cn(
              "rounded-xl border-2 border-dashed p-5 text-center transition-colors",
              dragging ? "border-foreground bg-muted/50" : "border-border bg-muted/20"
            )}
          >
            <FileSpreadsheet className="mx-auto h-8 w-8 text-muted-foreground" aria-hidden="true" />
            {file ? (
              <div className="mt-2 flex items-center justify-center gap-2">
                <span className="text-sm font-medium text-foreground break-all">
                  {file.name}
                </span>
                <span className="text-xs text-muted-foreground">
                  ({Math.max(1, Math.round(file.size / 1024))} KB)
                </span>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    reset();
                  }}
                  className="p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted"
                  title="Remove file"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            ) : (
              <p className="mt-2 text-sm text-muted-foreground">
                Drag and drop a CSV file here, or browse from your device
              </p>
            )}
            <input
              ref={inputRef}
              type="file"
              accept=".csv,text/csv"
              className="sr-only"
              aria-label="CSV file"
              onChange={(e) => {
                pickFile(e.target.files?.[0]);
                e.target.value = "";
              }}
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => inputRef.current?.click()}
              className="mt-3 h-9 text-xs gap-1.5 rounded-xl"
            >
              <Upload className="h-4 w-4" />
              {file ? "Change File" : "Choose CSV File"}
            </Button>
          </div>

          {/* Validating indicator */}
          {validating && (
            <div className="flex items-center justify-center gap-2 py-3 text-xs text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span>Analyzing file and validating formats client-side...</span>
            </div>
          )}

          {/* ------------------------------------------------------------- */}
          {/* CLIENT-SIDE PRE-VALIDATION STATS & REPORT                     */}
          {/* ------------------------------------------------------------- */}
          {clientValidation && !serverReport && (
            <section
              aria-live="polite"
              aria-label="Client-side validation summary"
              className="space-y-3 rounded-xl border border-border/80 bg-card p-4 shadow-xs"
            >
              <div className="flex items-center justify-between gap-2 border-b border-border/60 pb-2.5">
                <div className="flex items-center gap-2">
                  <h3 className="text-xs font-semibold uppercase tracking-wider text-foreground">
                    Client Pre-Validation Stats
                  </h3>
                  {clientValidation.invalidRows === 0 ? (
                    <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/10 px-2 py-0.5 text-[10px] font-semibold text-emerald-600 dark:text-emerald-400">
                      <CheckCircle2 className="h-3 w-3" />
                      All Valid
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 rounded-full bg-red-500/10 px-2 py-0.5 text-[10px] font-semibold text-red-600 dark:text-red-400">
                      <FileWarning className="h-3 w-3" />
                      {clientValidation.invalidRows} Format Issue{clientValidation.invalidRows > 1 ? "s" : ""}
                    </span>
                  )}
                </div>

                {/* Download Error CSV Button (Client-side) */}
                {clientValidation.errors.length > 0 && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => downloadErrorsCsv(clientValidation.errors, "client_validation_errors")}
                    className="h-8 gap-1.5 text-xs text-red-600 dark:text-red-400 border-red-200 dark:border-red-900/60 hover:bg-red-50 dark:hover:bg-red-950/30"
                    title="Export CSV of detected validation errors"
                  >
                    <Download className="h-3.5 w-3.5" />
                    <span>Download Error CSV</span>
                  </Button>
                )}
              </div>

              {/* Stat Counters */}
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="rounded-xl border border-border/70 bg-muted/20 p-2.5">
                  <p className="text-lg font-bold text-foreground">{clientValidation.totalRows}</p>
                  <p className="text-[11px] text-muted-foreground">Total Rows</p>
                </div>
                <div className="rounded-xl border border-emerald-500/20 bg-emerald-500/5 p-2.5">
                  <p className="text-lg font-bold text-emerald-600 dark:text-emerald-400">
                    {clientValidation.validRows}
                  </p>
                  <p className="text-[11px] text-muted-foreground">Valid Rows</p>
                </div>
                <div className="rounded-xl border border-red-500/20 bg-red-500/5 p-2.5">
                  <p className="text-lg font-bold text-red-600 dark:text-red-400">
                    {clientValidation.invalidRows}
                  </p>
                  <p className="text-[11px] text-muted-foreground">Invalid Rows</p>
                </div>
              </div>

              {/* Status Notice */}
              {clientValidation.invalidRows === 0 ? (
                <div className="flex items-center gap-2 p-2.5 rounded-lg bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 text-xs">
                  <CheckCircle2 className="h-4 w-4 shrink-0" />
                  <span>All rows passed client syntax checks. Click &ldquo;Import Devices&rdquo; to send to server.</span>
                </div>
              ) : (
                <div className="flex items-start gap-2 p-2.5 rounded-lg bg-amber-500/10 text-amber-800 dark:text-amber-300 text-xs">
                  <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold">Review detected format errors below.</p>
                    <p className="text-[11px] text-muted-foreground mt-0.5">
                      You can download the error CSV to fix them in your spreadsheet, or proceed to provision only the {clientValidation.validRows} valid row(s).
                    </p>
                  </div>
                </div>
              )}

              {/* Client Errors Table */}
              {clientValidation.errors.length > 0 && (
                <div className="max-h-48 overflow-auto rounded-xl border border-border/80">
                  <table className="w-full text-xs">
                    <thead className="bg-muted/40 text-left sticky top-0 backdrop-blur-sm">
                      <tr>
                        <th scope="col" className="px-3 py-2 font-medium">Line</th>
                        <th scope="col" className="px-3 py-2 font-medium">Column</th>
                        <th scope="col" className="px-3 py-2 font-medium">Identifier</th>
                        <th scope="col" className="px-3 py-2 font-medium">Error Reason</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border/60">
                      {clientValidation.errors.map((e, i) => (
                        <tr key={`client-err-${e.row}-${i}`} className="align-top hover:bg-muted/20">
                          <td className="px-3 py-2 font-mono font-medium">{e.row}</td>
                          <td className="px-3 py-2 font-mono text-muted-foreground">{e.column || "—"}</td>
                          <td className="px-3 py-2 font-mono break-all text-muted-foreground">{e.identifier || "—"}</td>
                          <td className="px-3 py-2 text-red-600 dark:text-red-400 font-medium">{e.reason}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          )}

          {/* ------------------------------------------------------------- */}
          {/* SERVER IMPORT REPORT (AFTER API CALL)                          */}
          {/* ------------------------------------------------------------- */}
          {serverReport && (
            <section aria-live="polite" aria-label="Server import report" className="space-y-3 rounded-xl border border-border/80 bg-card p-4 shadow-xs">
              <div className="flex items-center justify-between gap-2 border-b border-border/60 pb-2.5">
                <div className="flex items-center gap-2">
                  <h3 className="text-xs font-semibold uppercase tracking-wider text-foreground">
                    Server Provisioning Results
                  </h3>
                  <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/10 px-2 py-0.5 text-[10px] font-semibold text-emerald-600 dark:text-emerald-400">
                    <CheckCircle2 className="h-3 w-3" />
                    Completed
                  </span>
                </div>

                {/* Download Server Errors CSV */}
                {serverReport.errors.length > 0 && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => downloadErrorsCsv(serverReport.errors, "server_provisioning_errors")}
                    className="h-8 gap-1.5 text-xs text-red-600 dark:text-red-400 border-red-200 dark:border-red-900/60 hover:bg-red-50 dark:hover:bg-red-950/30"
                    title="Export CSV of server rejection errors"
                  >
                    <Download className="h-3.5 w-3.5" />
                    <span>Download Server Error CSV</span>
                  </Button>
                )}
              </div>

              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="rounded-xl border border-border/70 bg-muted/20 p-2.5">
                  <p className="text-lg font-bold text-foreground">{serverReport.total_rows}</p>
                  <p className="text-[11px] text-muted-foreground">Processed</p>
                </div>
                <div className="rounded-xl border border-emerald-500/20 bg-emerald-500/5 p-2.5">
                  <p className="text-lg font-bold text-emerald-600 dark:text-emerald-400 inline-flex items-center gap-1">
                    <CheckCircle2 className="h-4 w-4" aria-hidden="true" />
                    {serverReport.successful_rows}
                  </p>
                  <p className="text-[11px] text-muted-foreground">Provisioned</p>
                </div>
                <div className="rounded-xl border border-red-500/20 bg-red-500/5 p-2.5">
                  <p className="text-lg font-bold text-red-600 dark:text-red-400">{serverReport.failed_rows}</p>
                  <p className="text-[11px] text-muted-foreground">Failed</p>
                </div>
              </div>

              {serverReport.errors.length > 0 && (
                <div className="max-h-48 overflow-auto rounded-xl border border-border/80">
                  <table className="w-full text-xs">
                    <thead className="bg-muted/40 text-left sticky top-0 backdrop-blur-sm">
                      <tr>
                        <th scope="col" className="px-3 py-2 font-medium">Line</th>
                        <th scope="col" className="px-3 py-2 font-medium">Column</th>
                        <th scope="col" className="px-3 py-2 font-medium">Identifier</th>
                        <th scope="col" className="px-3 py-2 font-medium">Server Rejection Reason</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border/60">
                      {serverReport.errors.map((e, i) => (
                        <tr key={`server-err-${e.row}-${i}`} className="align-top hover:bg-muted/20">
                          <td className="px-3 py-2 font-mono font-medium">{e.row}</td>
                          <td className="px-3 py-2 font-mono text-muted-foreground">{e.column || "—"}</td>
                          <td className="px-3 py-2 font-mono break-all text-muted-foreground">{e.identifier || "—"}</td>
                          <td className="px-3 py-2 text-red-600 dark:text-red-400 font-medium">{e.reason}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          )}
        </div>

        <DialogFooter className="gap-2 sm:justify-between pt-3 border-t border-border mt-3">
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={importMutation.isPending}
            className="h-9 px-4 text-xs"
          >
            {serverReport ? "Done" : "Cancel"}
          </Button>

          {!serverReport && (
            <Button
              type="button"
              onClick={submit}
              disabled={
                !file ||
                importMutation.isPending ||
                validating ||
                (clientValidation !== null && clientValidation.validRows === 0)
              }
              className="h-9 px-4 text-xs min-w-[140px] shadow-sm bg-zinc-950 text-white hover:bg-zinc-900 dark:bg-zinc-100 dark:text-zinc-950 dark:hover:bg-white"
            >
              {importMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Provisioning...
                </>
              ) : clientValidation && clientValidation.validRows > 0 ? (
                `Import ${clientValidation.validRows} Device${clientValidation.validRows > 1 ? "s" : ""}`
              ) : (
                "Import Devices"
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
